import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["flush", "layout", "maxWidth"],
  _excluded2 = ["className", "layout", "primaryContent", "primaryContentClassName", "_primaryContentWidth", "reverseOrder", "secondaryContent", "secondaryContentClassName", "secondaryContentWidth", "titleText"];
import PropTypes from 'prop-types';
import { cloneElement, isValidElement, PureComponent } from 'react';
import styled, { css } from 'styled-components';
import H4 from '../elements/headings/H4';
import UISplitLayout from '../layout/UISplitLayout';
import { EMPTY_STATE_PADDING_X, EMPTY_STATE_PADDING_Y } from 'HubStyleTokens/sizes';
import { toPx } from '../utils/Styles';
import { warnIfFragment } from '../utils/devWarnings';
import { jsx as _jsx } from "react/jsx-runtime";
// empty state styling is characterized by copious whitespace, per design
// vertical orientation differs to more closely match UIErrorMessage styles
const OUTER_MARGIN = 40;
const Outer = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(UISplitLayout, Object.assign({}, rest));
}).withConfig({
  displayName: "UIEmptyState__Outer",
  componentId: "sc-1nkt60j-0"
})(["margin:", " auto;margin-bottom:", ";margin-top:", ";max-width:", ";padding-left:", ";width:100%;"], props => props.layout === 'vertical' ? toPx(OUTER_MARGIN) : toPx(OUTER_MARGIN * 2), props => props.flush && '0', props => props.flush && '0', props => toPx(props.maxWidth), EMPTY_STATE_PADDING_X);

// @ts-expect-error Type UISplitLayout
const Inner = styled(UISplitLayout.defaultProps.InnerWrapper).withConfig({
  displayName: "UIEmptyState__Inner",
  componentId: "sc-1nkt60j-1"
})(["margin-left:-", ";margin-top:-", ";"], EMPTY_STATE_PADDING_X, EMPTY_STATE_PADDING_Y);
const SLOT_PADDING_STYLES = css(["margin:auto;padding-left:", ";padding-top:", ";"], EMPTY_STATE_PADDING_X, EMPTY_STATE_PADDING_Y);

// @ts-expect-error Type UISplitLayout
const PrimarySlot = styled(UISplitLayout.defaultProps.Primary).withConfig({
  displayName: "UIEmptyState__PrimarySlot",
  componentId: "sc-1nkt60j-2"
})(["", ";margin-top:0;text-align:", ";"], SLOT_PADDING_STYLES, props => props.layout === 'vertical' && 'center');

// @ts-expect-error Type UISplitLayout
const SecondarySlot = styled(UISplitLayout.defaultProps.Secondary).withConfig({
  displayName: "UIEmptyState__SecondarySlot",
  componentId: "sc-1nkt60j-3"
})(["", ";margin-bottom:", ";margin-top:0;text-align:", ";", ";"], SLOT_PADDING_STYLES, props => props.layout === 'vertical' && '20px', props => props.layout === 'vertical' && 'center', props => props.width && `width: ${props.width}px`);
export default class UIEmptyState extends PureComponent {
  constructor(...args) {
    super(...args);
    this.Primary = props => {
      const {
        layout,
        _primaryContentWidth,
        reverseOrder,
        secondaryContent
      } = this.props;
      const computedPrimaryContentWidth = secondaryContent ? _primaryContentWidth : '100%';
      return /*#__PURE__*/_jsx(PrimarySlot, Object.assign({}, props, {
        basis: layout === 'horizontal' ? computedPrimaryContentWidth : 'auto',
        grow: 9999,
        layout: layout,
        order: reverseOrder ? 1 : 0,
        shrink: 1
      }));
    };
    this.Secondary = props => {
      const {
        layout,
        secondaryContentWidth = UIEmptyState.defaultProps.secondaryContentWidth
      } = this.props;
      const computedPadding = parseInt(EMPTY_STATE_PADDING_X, 10);
      const computedSecondaryContentWidth = secondaryContentWidth + computedPadding;
      return /*#__PURE__*/_jsx(SecondarySlot, Object.assign({}, props, {
        basis: layout === 'horizontal' ? computedSecondaryContentWidth : 'auto',
        grow: 1,
        layout: layout,
        shrink: 1,
        width: layout === 'vertical' ? computedSecondaryContentWidth : undefined
      }));
    };
  }
  render() {
    const _this$props = this.props,
      {
        className,
        layout,
        primaryContent,
        primaryContentClassName,
        secondaryContent,
        secondaryContentClassName,
        titleText
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded2);
    let renderedHeading;
    // @ts-expect-error Formatted* components need types to detect isI18nElement
    if ( /*#__PURE__*/isValidElement(titleText) && !titleText.type.isI18nElement) {
      warnIfFragment(titleText, UIEmptyState.displayName, 'titleText');
      renderedHeading = titleText;
    } else if (titleText != null) {
      renderedHeading = /*#__PURE__*/_jsx(H4, {
        children: titleText
      });
    }
    warnIfFragment(primaryContent, UIEmptyState.displayName, 'primaryContent');
    const renderedPrimaryContent = [renderedHeading != null ? /*#__PURE__*/cloneElement(renderedHeading, {
      key: 'heading '
    }) : renderedHeading, /*#__PURE__*/isValidElement(primaryContent) ? /*#__PURE__*/cloneElement(primaryContent, {
      key: 'content'
    }) : primaryContent];
    return /*#__PURE__*/_jsx(Outer, Object.assign({}, rest, {
      className: className,
      layout: layout,
      orientation: layout,
      InnerWrapper: Inner,
      Primary: this.Primary,
      primaryClassName: primaryContentClassName,
      primaryContent: renderedPrimaryContent,
      Secondary: this.Secondary,
      secondaryClassName: secondaryContentClassName,
      secondaryContent: secondaryContent
    }));
  }
}
UIEmptyState.propTypes = {
  flush: PropTypes.bool,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  maxWidth: PropTypes.number,
  primaryContent: PropTypes.node.isRequired,
  primaryContentClassName: PropTypes.string,
  _primaryContentWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  reverseOrder: PropTypes.bool,
  secondaryContent: PropTypes.node,
  secondaryContentClassName: PropTypes.string,
  secondaryContentWidth: PropTypes.number,
  titleText: PropTypes.node
};
const defaultProps = {
  flush: false,
  layout: 'horizontal',
  maxWidth: 815,
  reverseOrder: false,
  secondaryContentWidth: 350,
  _primaryContentWidth: 200
};
UIEmptyState.defaultProps = defaultProps;
UIEmptyState.displayName = 'UIEmptyState';